/**
 * StickySearchBar
 * A sticky toolbar for UsersTab with search, filter toggle, view mode toggle, and create button
 * Mobile-optimized with responsive layout
 */

import React, { useState, useRef } from 'react';
import Button from '../common/Button';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// Icons
const SearchIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const FilterIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const HierarchyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const TableIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

function StickySearchBar({
  searchValue,
  onSearchChange,
  viewMode,
  onViewModeChange,
  hasActiveFilter,
  filterCount = 0,
  onFilterToggle,
  onCreateUser,
  canCreateUser = false,
  disabled = false
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef(null);

  // Handle search clear
  const handleClearSearch = () => {
    onSearchChange('');
    searchInputRef.current?.focus();
  };

  return (
    <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-gray-200 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mb-4">
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Search Input - grows to fill space */}
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            placeholder={isMobile ? "Search..." : "Search by name, email, or nickname..."}
            className={`w-full pl-10 pr-10 py-2.5 border rounded-lg text-sm transition-all duration-200
              ${isSearchFocused
                ? 'border-tenq-gold ring-2 ring-tenq-gold/20'
                : 'border-gray-300 hover:border-gray-400'
              }
              disabled:bg-gray-100 disabled:cursor-not-allowed
            `}
            disabled={disabled}
          />
          {/* Clear button */}
          {searchValue && (
            <button
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Filter Toggle Button */}
        <button
          onClick={onFilterToggle}
          className={`relative flex items-center justify-center p-2.5 rounded-lg border transition-all duration-200
            ${hasActiveFilter
              ? 'bg-tenq-gold/10 border-tenq-gold text-tenq-gold'
              : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
            }
          `}
          title={hasActiveFilter ? 'Filters active - click to modify' : 'Open filters'}
          disabled={disabled}
        >
          <FilterIcon />
          {/* Filter count badge */}
          {filterCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center bg-tenq-gold text-white text-xs font-medium rounded-full px-1">
              {filterCount}
            </span>
          )}
        </button>

        {/* View Mode Toggle - Hidden on very small screens */}
        <div className="hidden xs:inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
          <button
            onClick={() => !hasActiveFilter && onViewModeChange('hierarchy')}
            disabled={hasActiveFilter || disabled}
            className={`px-2.5 sm:px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5
              ${viewMode === 'hierarchy' && !hasActiveFilter
                ? 'bg-white text-tenq-gold shadow-sm'
                : hasActiveFilter
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-900'
              }
            `}
            title={hasActiveFilter ? 'Clear filters to use hierarchy view' : 'Hierarchy view'}
          >
            <HierarchyIcon />
            <span className="hidden sm:inline">Hierarchy</span>
          </button>
          <button
            onClick={() => onViewModeChange('table')}
            disabled={disabled}
            className={`px-2.5 sm:px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5
              ${viewMode === 'table'
                ? 'bg-white text-tenq-gold shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
            title="Table view"
          >
            <TableIcon />
            <span className="hidden sm:inline">Table</span>
          </button>
        </div>

        {/* Create Button */}
        {canCreateUser && (
          <>
            {/* Full button on desktop */}
            <Button
              variant="primary"
              onClick={onCreateUser}
              disabled={disabled}
              className="hidden sm:flex items-center gap-2"
            >
              <PlusIcon />
              <span>Create</span>
            </Button>
            {/* Icon-only button on mobile */}
            <button
              onClick={onCreateUser}
              disabled={disabled}
              className="sm:hidden flex items-center justify-center w-10 h-10 bg-tenq-gold text-white rounded-lg shadow-gold hover:bg-tenq-gold-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Create User"
            >
              <PlusIcon />
            </button>
          </>
        )}
      </div>

      {/* Mobile View Toggle - Shows below search on mobile */}
      <div className="xs:hidden mt-2 flex items-center justify-between">
        <span className="text-xs text-gray-500">View:</span>
        <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
          <button
            onClick={() => !hasActiveFilter && onViewModeChange('hierarchy')}
            disabled={hasActiveFilter || disabled}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors
              ${viewMode === 'hierarchy' && !hasActiveFilter
                ? 'bg-white text-tenq-gold shadow-sm'
                : hasActiveFilter
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:text-gray-900'
              }
            `}
          >
            Hierarchy
          </button>
          <button
            onClick={() => onViewModeChange('table')}
            disabled={disabled}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors
              ${viewMode === 'table'
                ? 'bg-white text-tenq-gold shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
          >
            Table
          </button>
        </div>
      </div>

      {/* Active filter indicator on mobile */}
      {hasActiveFilter && isMobile && (
        <div className="mt-2 text-xs text-gray-500 italic">
          Filters active - showing table view
        </div>
      )}
    </div>
  );
}

export default StickySearchBar;
